import { Alert, Button, Divider, List, Select, Skeleton, Modal } from "antd";
import Password from "antd/lib/input/Password";
import { useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import QRCode from "react-qr-code";

import ApiService from "../../Api.service";
import * as ApiUtils from "../../ApiUtils";
import UserContext from "../../features/userContext";
import {
  clearSessionStorage,
  componentMountCheck,
  getImageBeacon,
  getSessionToken,
  saveSessionToken,
  setPushSessionToken,
  verifyErrorAndShowMessage,
} from "../../helpers/helper.service";
import {
  Branding,
  DUOFactorList,
  forgotPasswordPayload,
  SSPRSupportedRecoveryFactors,
  unlockAccountPayload,
  unlockAccountStepTwoPayload,
  UserAuthorizeFactors,
  userInfoResponse,
  validateChallengePayload,
  TecproofVerificationResult,
  TecproofVerification,
} from "../../interfaces/api.interface";
import ssprService from "../../services/sspr.service";
import { openNotification } from "../Common/Notification";
import RichTextViewer from "../Common/RichTextViewer";
import "./FactorAuthorize.css";
import useInterval from "../Common/useInterval";

const getFactorIcon = (item: any): string => {
  switch (true) {
    case item?.factorType === ApiUtils.HOTPFactorType:
      return "../../hotpFactor_38x38.png";
    case item?.factorProvider === ApiUtils.DUOFactorProvider:
      return "../../duo_38x38.9944c099fa2327e9e4da2b33c9516593.png";
    case [ApiUtils.oktaPushFactorType, ApiUtils.oktaTOTPFactorType].includes(
      item?.factorType,
    ) && item?.factorProvider === ApiUtils.oktaPushFactorProvider:
      return "../../oktaVerify_38x38.ec588b98b16b2ddd8a0256ffbc3bab88.png";
    case item?.factorType === ApiUtils.questionFactorType:
      return "../../question_38x38.8453db9ea592a3015add7fb268491a3b.png";
    case item?.factorType === ApiUtils.smsFactorType:
      return "../../sms_38x38.4661fd6ccaded3a68b1803ac32b26592.png";
    case item?.factorType === ApiUtils.emailFactorType:
      return "../../email_38x38.b7ac950da2ba64f5886b21c9f97c7cc8.png";
    case item?.factorType === ApiUtils.googleTOTPFactorType:
      return "../../googleAuth_38x38.750e8cb4f65b1d8f3a844baa0ac99454.png";
    case item?.factorType === ApiUtils.callFactorType ||
      item?.factorType === ApiUtils.voiceFactorType:
      return "../../voicecall_38x38.98ee4e6eac4e482dc8b2db9eadafb031.png";
    case item?.factorProvider === ApiUtils.yubicoTOTPHardwareProvider:
      return "../../yubico.png";
    case item?.factorProvider === ApiUtils.RSATokenFactorProvider:
      return "../../rsa_38x38.44b8b7f07f63b71ca53c6164d07d933d.png";
    case [
      ApiUtils.CREDENTIFactorProvider,
      ApiUtils.authentifaceFactorProvider,
    ].includes(item?.factorProvider):
      return "../../credenti-favicon.png";
    default:
      return "";
  }
};

const factorTypeDisplayNames: Record<string, string> = {
  [ApiUtils.authentifaceFactorType]: "Credenti Face",
  [ApiUtils.HOTPFactorType]: "Feitian Hardware Token",
  [ApiUtils.DUOFactorType]: "DUO Security",
  [ApiUtils.questionFactorType]: "Security Question",
  [ApiUtils.smsFactorType]: "SMS Authentication",
  [ApiUtils.googleTOTPFactorType]: "Google Authenticator",
  [ApiUtils.emailFactorType]: "Email Authentication",
  [ApiUtils.callFactorType]: "Voice Call Authentication",
  [ApiUtils.voiceFactorType]: "Voice Call Authentication",
  [ApiUtils.yubicoTOTPHardwareType]: "Yubico Authentication",
  [ApiUtils.RSATokenFactorType]: "RSA Authentication",
};

const factorProviderDisplayNames: Record<string, string> = {
  [ApiUtils.DUOFactorProvider]: "DUO Security",
  [ApiUtils.oktaTOTPFactorProvider]: "OKTA Verify",
  [ApiUtils.yubicoTOTPHardwareProvider]: "Yubico Authentication",
  [ApiUtils.RSATokenFactorProvider]: "RSA Authentication",
  [ApiUtils.CREDENTIFactorProvider]: "Credenti Face Authentication",
};

function getFactorDisplayName(item: UserAuthorizeFactors): string {
  if (
    item.factorType === ApiUtils.oktaPushFactorType &&
    item.factorProvider === ApiUtils.oktaPushFactorProvider
  ) {
    return "OKTA Verify With Push";
  }
  if (
    item.factorType === ApiUtils.oktaTOTPFactorType &&
    item.factorProvider === ApiUtils.oktaTOTPFactorProvider
  ) {
    return "OKTA Verify";
  }
  return (
    factorTypeDisplayNames[item.factorType] ||
    factorProviderDisplayNames[item.factorProvider] ||
    item.factorType
  );
}

const getFactorHint = (factor: any): string => {
  switch (true) {
    case Boolean(factor?.hint):
      return factor.hint;
    case [ApiUtils.HOTPFactorType, ApiUtils.oktaTOTPFactorType].includes(
      factor?.factorType,
    ) || factor?.factorProvider === ApiUtils.RSATokenFactorProvider:
      return "Enter pass code";
    case factor?.factorProvider === ApiUtils.yubicoTOTPHardwareProvider &&
      factor?.factorType === ApiUtils.yubicoTOTPHardwareType:
      return "Click here, then tap your YubiKey";
    case factor?.factorType === ApiUtils.FACE_AUTHFactoryType:
      return "Scan the QrCode to begin the facial authentication";
    default:
      return "";
  }
};

export default function FactorAuthorize() {
  const focusInputField = () => {
    document.getElementById("codeTextField")?.focus();
  };
  const [selectedListItem, setSelectedListItem] = useState(null);
  const handleItemClick = (item: any) => {
    setSelectedListItem(item);
  };

  const selectedOperation = useParams().operation;
  const [factorListVisible, setFactorListVisible] = useState(true);
  const location = useLocation();
  const [errorDescription, setErrorDescription] = useState("");
  const [userState, setUserState] = useContext(UserContext);
  const [pushExpiryTime, setPushExpiryTime] = useState(new Date());
  const [selectedFactor, setSelectedFactor] = useState<UserAuthorizeFactors>();
  const [buttonLoadings, setButtonLoadings] = useState<boolean[]>([
    false,
    false,
    false,
  ]);
  const [duoButtonLoadings, setDUOButtonLoadings] = useState<boolean[]>([
    false,
    false,
    false,
    false,
  ]);
  const [duoPasscodeSectionVisi, setDuoPasscodeSectionVisi] = useState(false);
  const [duoSectionVisi, setDuoSectionVisi] = useState(false);
  const [challengeNumber, setChallengeNumber] = useState("");
  const [qrCodeValue, setQrCodeValue] = useState("");
  const [retryTecproof, setRetryTecproof] = useState(false);
  const [countDown, setCountDown] = useState(60); //60 seconds
  const setButtonLoading = (index: number) => {
    setButtonLoadings((prevLoadings) => {
      const newLoadings = [...prevLoadings];
      newLoadings[index] = true;
      return newLoadings;
    });
  };

  const stopAllButtonLoadings = () => {
    setButtonLoadings((prevLoadings) => {
      const newLoadings = [...prevLoadings];
      newLoadings[0] = false;
      newLoadings[1] = false;
      newLoadings[2] = false;
      return newLoadings;
    });
  };

  const [sendCodeButtonLabel, setSendCodeButtonLabel] = useState("Send Code");
  useMemo(() => {
    setErrorDescription("");
    setSendCodeButtonLabel("Send Code");
  }, [selectedFactor?.factorType]);
  const [sendPushButtonLabel, setSendPushButtonLabel] = useState("Send Push");
  useMemo(() => {
    setSendPushButtonLabel("Send push");
    setErrorDescription("");
  }, [selectedFactor?.factorType]);

  const [defaultFactorToShow, setDefaultFactorToShow] = useState("");
  const [brandingDetails, setBrandingDetails] = useState<Branding>();
  const [authorizedFactors, setAuthorizedFactors] =
    useState<UserAuthorizeFactors[]>();
  const [verificationCode, setVerificationCode] = useState("");
  let navigate = useNavigate();
  const [userInfo, setUserInfo] = useState<userInfoResponse>();
  useEffect(() => {
    setChallengeNumber("");
    componentMountCheck(location.state as string);
    let temp = SSPRSupportedRecoveryFactors.filter(
      (item) => item.key === userState.userInfo.config.default_factor_to_show,
    );
    setAuthorizedFactors(userState.userInfo.factor);
    setUserInfo(userState.userInfo);
    setBrandingDetails(ssprService.getBrandingDetails());
    if (
      !!ssprService.getBrandingDetails() &&
      !!ssprService.getBrandingDetails().background_image
    )
      document.body.style.backgroundImage = `url(${ssprService.getBrandingDetails().background_image}?v=1)`;
    if (!!temp && temp.length > 0) {
      setDefaultFactorToShow(temp[0].factorType + "-" + temp[0].provider);
      const factorType = temp[0].factorType;
      const factorProvider = temp[0].provider;
      let defaultFactorExists = userState.userInfo.factor.filter((element) => {
        return (
          element.factorType === factorType &&
          element.factorProvider === factorProvider
        );
      });
      if (!!defaultFactorExists && defaultFactorExists.length > 0) {
        setImageBeaconSrc(
          factorType === ApiUtils.HOTPFactorType
            ? "../../hotpFactor_70x70.png"
            : factorType === ApiUtils.DUOFactorType
              ? "../../duo_70x70.e2baf65d97a5d6f2c083e14d4717ccf4.png"
              : (factorType === ApiUtils.oktaPushFactorType ||
                    factorType === ApiUtils.oktaTOTPFactorType) &&
                  factorProvider === ApiUtils.oktaPushFactorProvider
                ? "../../oktaVerify_70x70.00aa672ea67517ca19e2b110d650e03f.png"
                : factorType === ApiUtils.smsFactorType
                  ? "../../sms_70x70.30e2a05096c1192ff1f96cf11acfdf17.png"
                  : factorType === ApiUtils.googleTOTPFactorType
                    ? "../../googleAuth_38x38.750e8cb4f65b1d8f3a844baa0ac99454.png"
                    : factorType === ApiUtils.callFactorType ||
                        factorType === ApiUtils.voiceFactorType
                      ? "../../voicecall_70x70.b651a1f230ee1aea148c87773d533faf.png"
                      : factorType === ApiUtils.emailFactorType
                        ? "../../email_38x38.b7ac950da2ba64f5886b21c9f97c7cc8.png"
                        : factorType === ApiUtils.yubicoTOTPHardwareType &&
                            factorProvider ===
                              ApiUtils.yubicoTOTPHardwareProvider
                          ? "../../yubico@2x.png"
                          : factorProvider === ApiUtils.RSATokenFactorProvider
                            ? "../../rsa_70x70.e01fbd6241936eeb9cdf15c72cde49e9.png"
                            : "../../question_38x38.8453db9ea592a3015add7fb268491a3b.png",
        );
        if (
          temp[0].factorType + "-" + temp[0].provider &&
          selectedFactor?.factorType + "-" + selectedFactor?.factorProvider &&
          !!userState.userInfo.factor
        ) {
          for (const object of userState.userInfo.factor) {
            if (
              object.factorType + "-" + object.factorProvider ===
              temp[0].factorType + "-" + temp[0].provider
            ) {
              setSelectedFactor(object);
              sessionStorage.setItem(
                "selectedFactor",
                object.factorType + ":" + object.factorProvider,
              );
              focusInputField();
              break;
            }
          }
        }
      } else {
        setDefaultFactorToShow("");
      }
    }
    if (userState.userInfo.currentStep === "ADDITIONAL_MFA") {
      if (
        userState.userInfo.factor.length === 1 &&
        userState.userInfo.factor[0].factorType === ApiUtils.questionFactorType
      ) {
        focusInputField();
        setFactorListVisible(false);
        setSelectedFactor(userState.userInfo.factor[0]);
        sessionStorage.setItem(
          "selectedFactor",
          userState.userInfo.factor[0].factorType +
            ":" +
            userState.userInfo.factor[0].factorProvider,
        );
      }
    }
    setLoading(false);
  }, []);
  const content = {
    ops: [{ insert: brandingDetails?.instructions }],
  };
  function handleAdditionalVerification(factors: UserAuthorizeFactors[]) {
    if (
      factors.length === 1 &&
      factors[0].factorType === ApiUtils.questionFactorType
    ) {
      setFactorListVisible(false);
      setSelectedFactor({
        factorProvider: factors[0].factorProvider,
        factorType: factors[0].factorType,
        hint: factors[0].hint,
        factorStatus: factors[0].factorStatus,
      });
      focusInputField();
      sessionStorage.setItem(
        "selectedFactor",
        factors[0].factorType + ":" + factors[0].factorProvider,
      );
      return;
    }
    for (var i = 0; i < factors.length; i++) {
      if (factors[i].factorType === ApiUtils.questionFactorType) {
        setAuthorizedFactors([factors[i]]);
        focusInputField();
        setFactorListVisible(false);
        setSelectedFactor({
          factorProvider: factors[i].factorProvider,
          factorType: factors[i].factorType,
          hint: factors[i].hint,
          factorStatus: factors[i].factorStatus,
        });
        sessionStorage.setItem(
          "selectedFactor",
          factors[i].factorType + ":" + factors[i].factorProvider,
        );
        return;
      }
    }
    setAuthorizedFactors(factors);
  }

  async function handleGenerateTecproof() {
    setQrCodeValue("");
    setErrorDescription("");
    setButtonLoading(0);
    let body: TecproofVerification = {
      sessionToken: getSessionToken(),
      email: (userState.userInfo as any).email ?? "",
      factor_type: selectedFactor?.factorType ?? "",
      factor_provider: selectedFactor?.factorProvider ?? "",
      actionType:
        userState.selectedOperation === ApiUtils.forgotPasswordLabel
          ? "forgotPassword"
          : userState.selectedOperation === ApiUtils.unlockAccountLabel
            ? "unlockAccount"
            : "changePassword",
    };
    await ApiService.post(
      `${ApiUtils.base_url}${ApiUtils.Urls.tecproofGenerate}`,
      body,
    )
      .then((response) => {
        if (!!response.access_token) {
          if (!!response.hasOwnProperty("sessionToken"))
            saveSessionToken(response.sessionToken);
          setQrCodeValue(response["redirect_url"]);
        } else {
          setRetryTecproof(true);
          setErrorDescription(
            "Error in starting a new Tecproof facial authentication session",
          );
        }
      })
      .catch((errror) => {
        setErrorDescription(
          "Error in starting a new Tecproof facial authentication session",
        );
        setRetryTecproof(true);
      })
      .finally(() => {
        stopAllButtonLoadings();
      });
  }
  async function handleVerifyTecproof() {
    setErrorDescription("");
    try {
      let payload: TecproofVerificationResult = {
        sessionToken: getSessionToken(),
        email: (userState.userInfo as any).email ?? "",
        factor_type: selectedFactor?.factorType ?? "",
        factor_provider: selectedFactor?.factorProvider ?? "",
        actionType:
          userState.selectedOperation === ApiUtils.forgotPasswordLabel
            ? "forgotPassword"
            : userState.selectedOperation === ApiUtils.unlockAccountLabel
              ? "unlockAccount"
              : "changePassword",
      };
      let data = await ApiService.post(
        `${ApiUtils.base_url}${ApiUtils.Urls.tecproofVerify}`,
        payload,
      );

      if (!data.inProgress) {
        if (data.success) {
          if (data.sessionToken) saveSessionToken(data.sessionToken ?? "");
          if (userState.selectedOperation === ApiUtils.unlockAccountLabel) {
            setQrCodeValue("");
            await handleSendCodeClick(
              selectedFactor?.factorType ?? ApiUtils.FACE_AUTHFactoryType,
            );
          } else {
            return navigate(ApiUtils.recoveryOptionRoute, {
              state: ApiUtils.recoveryOptionRoute,
            });
          }
        } else {
          setRetryTecproof(true);
          setErrorDescription(
            "There seems to be some error. Please contact the admin.",
          );
        }
      }
    } catch (error) {
      console.log(error);
      setErrorDescription(
        "There was an unknown error in Face Auth verification. Retry or choose a different factor. " +
          error,
      );
    }
  }
  useInterval(
    () => {
      handleVerifyTecproof();
    },
    selectedFactor?.factorProvider === ApiUtils.CREDENTIFactorProvider &&
      selectedFactor?.factorType === ApiUtils.FACE_AUTHFactoryType &&
      !!qrCodeValue
      ? 10000
      : null,
  );
  useInterval(
    () => {
      if (countDown === 0) {
        setRetryTecproof(true);
      } else {
        setCountDown(countDown - 1);
      }
    },
    selectedFactor?.factorProvider === ApiUtils.CREDENTIFactorProvider &&
      selectedFactor?.factorType === ApiUtils.FACE_AUTHFactoryType &&
      !retryTecproof &&
      countDown > -1
      ? 1000
      : null,
  );
  useEffect(() => {
    if (
      selectedFactor?.factorProvider === ApiUtils.authentifaceFactorProvider
    ) {
      handleSendCodeClick(selectedFactor.factorType);
    } else if (
      selectedFactor?.factorProvider === ApiUtils.CREDENTIFactorProvider &&
      selectedFactor?.factorType === ApiUtils.FACE_AUTHFactoryType &&
      !qrCodeValue
    ) {
      handleGenerateTecproof();
    }
  }, [selectedFactor]);
  async function handleSendCodeClick(
    action: string,
    duoFactorType: string = "",
  ) {
    if (
      selectedFactor?.factorType === ApiUtils.questionFactorType &&
      !verificationCode
    ) {
      openNotification("info", ApiUtils.answerEmptyMsg);
      stopAllButtonLoadings();
      return;
    }
    if (
      (selectedFactor?.factorType === ApiUtils.HOTPFactorType ||
        (selectedFactor?.factorProvider === ApiUtils.DUOTOTPFactorProvider &&
          selectedFactor.factorType === ApiUtils.DUOFactorType) ||
        (selectedFactor?.factorProvider === ApiUtils.RSATokenFactorProvider &&
          selectedFactor?.factorType === ApiUtils.RSATokenFactorType) ||
        selectedFactor?.factorType === ApiUtils.googleTOTPFactorType ||
        selectedFactor?.factorType === ApiUtils.oktaTOTPFactorType ||
        (selectedFactor?.factorType === ApiUtils.yubicoTOTPHardwareType &&
          selectedFactor?.factorProvider ===
            ApiUtils.yubicoTOTPHardwareProvider)) &&
      !verificationCode
    ) {
      openNotification("info", ApiUtils.passcodeEmptyMsg);
      stopAllButtonLoadings();
      return;
    }
    if (userState.selectedOperation !== ApiUtils.unlockAccountLabel) {
      let inputData: forgotPasswordPayload = {
        actionType:
          userState.selectedOperation === ApiUtils.forgotPasswordLabel
            ? "forgotPassword"
            : "changePassword",
        answer:
          selectedFactor?.factorType === ApiUtils.questionFactorType
            ? verificationCode
            : "",
        factor_type: !!duoFactorType
          ? duoFactorType
          : !!selectedFactor?.factorType
            ? selectedFactor?.factorType
            : "",
        one_time_code:
          selectedFactor?.factorType === ApiUtils.HOTPFactorType ||
          (selectedFactor?.factorProvider === ApiUtils.DUOTOTPFactorProvider &&
            selectedFactor.factorType === ApiUtils.DUOFactorType) ||
          (selectedFactor?.factorProvider === ApiUtils.RSATokenFactorProvider &&
            selectedFactor?.factorType === ApiUtils.RSATokenFactorType) ||
          selectedFactor?.factorType === ApiUtils.googleTOTPFactorType ||
          selectedFactor?.factorType === ApiUtils.oktaTOTPFactorType ||
          (selectedFactor?.factorType === ApiUtils.yubicoTOTPHardwareType &&
            selectedFactor?.factorProvider ===
              ApiUtils.yubicoTOTPHardwareProvider)
            ? verificationCode
            : "",
        factor_provider: !!duoFactorType
          ? "DUO"
          : !!selectedFactor?.factorProvider
            ? selectedFactor?.factorProvider
            : "",
        username: !!userInfo?.user_name ? userInfo.user_name : "",
        sessionToken: getSessionToken(),
      };
      await ApiService.post(
        `${ApiUtils.base_url}${ApiUtils.Urls.forgotPassword}`,
        inputData,
      )
        .then((response) => {
          stopAllButtonLoadings();
          setErrorDescription("");
          setDUOButtonLoadings([false, false, false, false]);
          if (
            !!verifyErrorAndShowMessage(
              response,
              ApiUtils.apiforgotPasswordErrorMsg,
            )
          ) {
            if (response.hasOwnProperty("factorResult")) {
              if (response.factorResult === "ANSWER_RECOVERY_QUESTION") {
                setChallengeNumber("");
                setVerificationCode("");
                setSelectedFactor({
                  factorProvider: "",
                  factorStatus: "",
                  factorType: "",
                  hint: "",
                });
                if (!!response.hasOwnProperty("sessionToken"))
                  saveSessionToken(response.sessionToken);
                setUserState((prevState) => ({
                  userInfo: {
                    config: prevState.userInfo.config,
                    currentStep: "ANSWER_RECOVERY_QUESTION",
                    factor: response.factors,
                    recovery_question: response.recovery_question,
                    sessionToken: userState.userInfo.sessionToken,
                    show_change_password:
                      userState.userInfo.show_change_password,
                    show_forgot_password:
                      userState.userInfo.show_forgot_password,
                    show_unlock_account: userState.userInfo.show_unlock_account,
                    user_name: userState.userInfo.user_name,
                  },
                  selectedOperation: userState.selectedOperation,
                }));
                navigate(ApiUtils.recoveryQuestionRoute, {
                  state: ApiUtils.recoveryQuestionRoute,
                });
              } else if (response.factorResult === "ADDITIONAL_MFA") {
                setChallengeNumber("");
                setVerificationCode("");
                setSelectedFactor({
                  factorProvider: "",
                  factorStatus: "",
                  factorType: "",
                  hint: "",
                });
                if (!!response.hasOwnProperty("sessionToken"))
                  saveSessionToken(response.sessionToken);
                setUserState((prevState) => ({
                  userInfo: {
                    config: prevState.userInfo.config,
                    currentStep: "ADDITIONAL_MFA",
                    factor: response.factors,
                    recovery_question: "",
                    sessionToken: userState.userInfo.sessionToken,
                    show_change_password:
                      userState.userInfo.show_change_password,
                    show_forgot_password:
                      userState.userInfo.show_forgot_password,
                    show_unlock_account: userState.userInfo.show_unlock_account,
                    user_name: userState.userInfo.user_name,
                  },
                  selectedOperation: userState.selectedOperation,
                }));
                handleAdditionalVerification(response.factors);
              } else if (response.factorResult === "PASSWORD_RESET") {
                setChallengeNumber("");
                setVerificationCode("");
                if (!!response.hasOwnProperty("sessionToken"))
                  saveSessionToken(response.sessionToken);
                navigate(ApiUtils.recoveryOptionRoute, {
                  state: ApiUtils.recoveryOptionRoute,
                });
              } else if (
                response.factorResult === "CHALLENGE" &&
                [
                  ApiUtils.authentifaceFactorType,
                  ApiUtils.smsFactorType,
                  ApiUtils.callFactorType,
                  ApiUtils.voiceFactorType,
                  ApiUtils.emailFactorType,
                ].includes(action)
              ) {
                saveSessionToken(response.sessionToken);
                setChallengeNumber("");
                setErrorDescription("");
                if (action === ApiUtils.authentifaceFactorType) {
                  Modal.confirm({
                    title: "Verify Your Identity",
                    content:
                      "You’ll be redirected to AuthentiFace to verify your identity.",
                    onOk() {
                      window.location.href = response.verify_url;
                    },
                  });
                } else {
                  setSendCodeButtonLabel("Sent");
                  focusInputField();
                  openNotification("info", ApiUtils.codeSentSuccessMsg);
                  setTimeout(() => {
                    var factor_session =
                      sessionStorage.getItem("selectedFactor");
                    if (
                      factor_session &&
                      (factor_session?.indexOf(ApiUtils.smsFactorType) >= 0 ||
                        factor_session?.indexOf(ApiUtils.callFactorType) >= 0 ||
                        factor_session?.indexOf(ApiUtils.voiceFactorType) >=
                          0 ||
                        factor_session?.indexOf(ApiUtils.emailFactorType) >= 0)
                    ) {
                      setErrorDescription(
                        "Haven't received an " +
                          action +
                          "? To try again, click Re-send code.",
                      );
                      setSendCodeButtonLabel("Re-send code");
                    }
                  }, 30000);
                }
              } else if (
                action === ApiUtils.oktaPushFactorType &&
                response.factorResult === "WAITING"
              ) {
                setChallengeNumber(response.hint);
                setPushSessionToken(response.sessionToken);
                saveSessionToken(response.sessionToken);
                setSendPushButtonLabel("Push sent!");
                setPushExpiryTime(new Date(response.expiresAt));
                openNotification("info", ApiUtils.pushSentSuccessMsg);
                setTimeout(() => {
                  var factor_session = sessionStorage.getItem("selectedFactor");
                  if (
                    selectedFactor?.factorProvider ===
                      ApiUtils.oktaPushFactorProvider &&
                    !!factor_session &&
                    factor_session.indexOf("push") >= 0
                  ) {
                    setErrorDescription(
                      "Haven't received a push notification yet? Try opening the Okta Verify App on your phone.",
                    );
                    setSendPushButtonLabel("Re-send Push");
                  }
                }, 30000);
                handlePollAPI();
              } else if (
                (action === ApiUtils.HOTPFactorType ||
                  action === ApiUtils.RSATokenFactorType ||
                  action === ApiUtils.yubicoTOTPHardwareType ||
                  action === ApiUtils.oktaTOTPFactorType ||
                  action === ApiUtils.questionFactorType) &&
                response.factorResult.toUpperCase() === "SUCCESS"
              ) {
                setChallengeNumber("");
                saveSessionToken(response.sessionToken);
                openNotification("success", ApiUtils.passcodeVerifySuccesMsg);
                if (!!userInfo?.recovery_question) {
                  navigate(ApiUtils.recoveryQuestionRoute, {
                    state: ApiUtils.recoveryQuestionRoute,
                  });
                } else {
                  navigate(ApiUtils.recoveryOptionRoute, {
                    state: ApiUtils.recoveryOptionRoute,
                  });
                }
              } else if (response.factorResult.toUpperCase() === "PASSCODE") {
                setChallengeNumber("");
                if (response.hasOwnProperty("message")) {
                  openNotification("error", response.message);
                } else openNotification("error", response.factorResult);
              } else {
                setChallengeNumber("");
                focusInputField();
                saveSessionToken(response.sessionToken);
                openNotification("info", response.factorResult);
              }
            } else if (response.hasOwnProperty("nextStep")) {
              saveSessionToken(response.sessionToken);
              openNotification("success", ApiUtils.answerVerifySuccesMsg);
              if (!!userInfo?.recovery_question) {
                navigate(ApiUtils.recoveryQuestionRoute, {
                  state: ApiUtils.recoveryQuestionRoute,
                });
              } else {
                navigate(ApiUtils.recoveryOptionRoute, {
                  state: ApiUtils.recoveryOptionRoute,
                });
              }
            } else {
              stopAllButtonLoadings();
              openNotification("info", ApiUtils.apiforgotPasswordErrorMsg);
            }
          } else {
            if (response.hasOwnProperty("errorSummary")) {
              setErrorDescription(response.errorSummary);
            }
            focusInputField();
          }
        })
        .catch((error) => {
          console.error("Error: ", error);
          stopAllButtonLoadings();
          setErrorDescription(ApiUtils.APITimeoutErrorMsg);
        })
        .finally(() => {
          stopAllButtonLoadings();
        });
    } else {
      let payload: unlockAccountPayload = {
        factor_type: !!duoFactorType
          ? duoFactorType
          : !!selectedFactor?.factorType
            ? selectedFactor?.factorType
            : "",
        answer:
          selectedFactor?.factorType === ApiUtils.questionFactorType
            ? verificationCode
            : "",
        one_time_code:
          selectedFactor?.factorType === ApiUtils.HOTPFactorType ||
          selectedFactor?.factorProvider ===
            ApiUtils.yubicoTOTPHardwareProvider ||
          selectedFactor?.factorType === ApiUtils.yubicoTOTPHardwareType ||
          selectedFactor?.factorType === ApiUtils.googleTOTPFactorType ||
          selectedFactor?.factorType === ApiUtils.oktaTOTPFactorType
            ? verificationCode
            : "",
        factor_provider: !!duoFactorType
          ? "DUO"
          : !!selectedFactor?.factorProvider
            ? selectedFactor?.factorProvider
            : "",
        username: userState.userInfo.user_name,
        sessionToken: getSessionToken(),
      };
      await ApiService.post(
        `${ApiUtils.base_url}${ApiUtils.Urls.unlockAccount}`,
        payload,
      )
        .then((response) => {
          stopAllButtonLoadings();
          setDUOButtonLoadings([false, false, false, false]);
          if (
            !!verifyErrorAndShowMessage(
              response,
              ApiUtils.apiUnlockAccountErrorMsg,
            )
          ) {
            if (
              !!response.hasOwnProperty("next step") &&
              response["next step"] === "validate two step factor"
            ) {
              setChallengeNumber("");
              saveSessionToken(response.sessionToken);
              setSendCodeButtonLabel("Sent");
              setErrorDescription("");
              setTimeout(() => {
                var factor_session = sessionStorage.getItem("selectedFactor");
                if (
                  factor_session &&
                  (factor_session?.indexOf(ApiUtils.smsFactorType) >= 0 ||
                    factor_session?.indexOf(ApiUtils.callFactorType) >= 0 ||
                    factor_session?.indexOf(ApiUtils.voiceFactorType) >= 0 ||
                    factor_session?.indexOf(ApiUtils.emailFactorType) >= 0)
                ) {
                  setErrorDescription(
                    "Haven't received an " +
                      action +
                      "? To try again, click Re-send code.",
                  );
                  setSendCodeButtonLabel("Re-send code");
                }
              }, 30000);
              openNotification(
                "success",
                selectedFactor?.factorType === "sms" ||
                  selectedFactor?.factorType === ApiUtils.emailFactorType ||
                  selectedFactor?.factorType === ApiUtils.callFactorType ||
                  selectedFactor?.factorType === ApiUtils.voiceFactorType
                  ? ApiUtils.codeSentSuccessMsg
                  : "",
              );
              focusInputField();
            }
            if (
              response.factorResult === "CHALLENGE" &&
              (action === ApiUtils.smsFactorType ||
                action === ApiUtils.callFactorType ||
                action === ApiUtils.voiceFactorType ||
                action === ApiUtils.emailFactorType)
            ) {
              setChallengeNumber("");
              saveSessionToken(response.sessionToken);
              setSendCodeButtonLabel("Sent");
              setErrorDescription("");
              setTimeout(() => {
                var factor_session = sessionStorage.getItem("selectedFactor");
                if (
                  factor_session &&
                  (factor_session?.indexOf(ApiUtils.smsFactorType) >= 0 ||
                    factor_session?.indexOf(ApiUtils.callFactorType) >= 0 ||
                    factor_session?.indexOf(ApiUtils.voiceFactorType) >= 0 ||
                    factor_session?.indexOf(ApiUtils.emailFactorType) >= 0)
                ) {
                  setErrorDescription(
                    "Haven't received an " +
                      action +
                      "? To try again, click Re-send code.",
                  );
                  setSendCodeButtonLabel("Re-send code");
                }
              }, 30000);
              openNotification("info", ApiUtils.codeSentSuccessMsg);
              focusInputField();
            } else if (
              action === ApiUtils.oktaPushFactorType &&
              response.factorResult === "WAITING"
            ) {
              setChallengeNumber(response.hint);
              setPushSessionToken(response.sessionToken);
              setSendPushButtonLabel("Push sent!");
              setPushExpiryTime(new Date(response.expiresAt));
              openNotification("info", ApiUtils.pushSentSuccessMsg);
              setTimeout(() => {
                var factor_session = sessionStorage.getItem("selectedFactor");
                if (
                  selectedFactor?.factorProvider ===
                    ApiUtils.oktaPushFactorProvider &&
                  !!factor_session &&
                  factor_session.indexOf("push") >= 0
                ) {
                  setErrorDescription(
                    "Haven't received a push notification yet? Try opening the Okta Verify App on your phone.",
                  );
                  setSendPushButtonLabel("Re-send Push");
                }
              }, 30000);
              handlePollAPI();
            } else if (
              response.hasOwnProperty("recoveryType") &&
              response.recoveryType === "UNLOCK"
            ) {
              setChallengeNumber("");
              saveSessionToken(response.sessionToken);
              openNotification(
                "success",
                selectedFactor?.factorType === ApiUtils.questionFactorType
                  ? ApiUtils.answerVerifySuccesMsg
                  : ApiUtils.passcodeVerifySuccesMsg,
              );
              if (response.hasOwnProperty("Next")) {
                userState.userInfo.recovery_question = response.Next;
                setUserState(userState);
                navigate(ApiUtils.recoveryQuestionRoute, {
                  state: ApiUtils.recoveryQuestionRoute,
                });
              } else {
                navigate(ApiUtils.confirmationRoute, {
                  state: ApiUtils.confirmationRoute,
                });
              }
            } else if (response.factorResult === "ADDITIONAL_MFA") {
              setChallengeNumber("");
              setVerificationCode("");
              setSelectedFactor({
                factorProvider: "",
                factorStatus: "",
                factorType: "",
                hint: "",
              });
              if (!!response.hasOwnProperty("sessionToken"))
                saveSessionToken(response.sessionToken);
              setUserState((prevState) => ({
                userInfo: {
                  config: prevState.userInfo.config,
                  currentStep: "ADDITIONAL_MFA",
                  factor: response.factors,
                  recovery_question: "",
                  sessionToken: userState.userInfo.sessionToken,
                  show_change_password: userState.userInfo.show_change_password,
                  show_forgot_password: userState.userInfo.show_forgot_password,
                  show_unlock_account: userState.userInfo.show_unlock_account,
                  user_name: userState.userInfo.user_name,
                },
                selectedOperation: userState.selectedOperation,
              }));
              handleAdditionalVerification(response.factors);
            } else if (response.factorResult === "PASSWORD_RESET") {
              setChallengeNumber("");
              setVerificationCode("");
              if (!!response.hasOwnProperty("sessionToken"))
                saveSessionToken(response.sessionToken);
              navigate(ApiUtils.recoveryOptionRoute, {
                state: ApiUtils.recoveryOptionRoute,
              });
            } else if (response.factorResult.toUpperCase() === "PASSCODE") {
              setChallengeNumber("");
              if (response.hasOwnProperty("message")) {
                openNotification("error", response.message);
              } else openNotification("error", response.factorResult);
            } else if (
              response.hasOwnProperty("factorResult") &&
              !!response.factorResult
            ) {
              setChallengeNumber("");
              openNotification("info", response.factorResult);
              focusInputField();
            }
          } else {
            if (response.hasOwnProperty("errorSummary")) {
              setErrorDescription(response.errorSummary);
            }
            focusInputField();
          }
        })
        .catch((error) => {
          stopAllButtonLoadings();
          setErrorDescription(ApiUtils.APITimeoutErrorMsg);
        })
        .finally(() => {
          stopAllButtonLoadings();
        });
    }
  }

  const handlePollAPI = () => {
    let pollResponse: any;
    setTimeout(
      () =>
        ApiService.get(
          `${ApiUtils.base_url}${ApiUtils.Urls.pollForPushSuccess(!!userInfo?.user_name ? userInfo.user_name : "")}`,
        )
          .then((response) => {
            if (
              sessionStorage.getItem("selectedFactor") === "push:OKTA" &&
              !!response &&
              !!verifyErrorAndShowMessage(
                response,
                ApiUtils.apiPollForSuccessErrorMsg,
              )
            ) {
              if (response.factorResult === "ANSWER_RECOVERY_QUESTION") {
                setChallengeNumber("");
                setVerificationCode("");
                sessionStorage.setItem("selectedFactor", "");
                setSelectedFactor({
                  factorProvider: "",
                  factorStatus: "",
                  factorType: "",
                  hint: "",
                });
                if (!!response.hasOwnProperty("sessionToken"))
                  saveSessionToken(response.sessionToken);
                setUserState((prevState) => ({
                  userInfo: {
                    config: prevState.userInfo.config,
                    currentStep: response.factorResult,
                    factor: response.factors,
                    recovery_question: response.recovery_question,
                    sessionToken: userState.userInfo.sessionToken,
                    show_change_password:
                      userState.userInfo.show_change_password,
                    show_forgot_password:
                      userState.userInfo.show_forgot_password,
                    show_unlock_account: userState.userInfo.show_unlock_account,
                    user_name: userState.userInfo.user_name,
                  },
                  selectedOperation: userState.selectedOperation,
                }));
                navigate(ApiUtils.recoveryQuestionRoute, {
                  state: ApiUtils.recoveryQuestionRoute,
                });
              } else if (response.factorResult === "ADDITIONAL_MFA") {
                setChallengeNumber("");
                setVerificationCode("");
                sessionStorage.setItem("selectedFactor", "");
                setSelectedFactor({
                  factorProvider: "",
                  factorStatus: "",
                  factorType: "",
                  hint: "",
                });
                if (!!response.hasOwnProperty("sessionToken"))
                  saveSessionToken(response.sessionToken);
                setUserState((prevState) => ({
                  userInfo: {
                    config: prevState.userInfo.config,
                    currentStep: "ADDITIONAL_MFA",
                    factor: response.factors,
                    recovery_question: "",
                    sessionToken: userState.userInfo.sessionToken,
                    show_change_password:
                      userState.userInfo.show_change_password,
                    show_forgot_password:
                      userState.userInfo.show_forgot_password,
                    show_unlock_account: userState.userInfo.show_unlock_account,
                    user_name: userState.userInfo.user_name,
                  },
                  selectedOperation: userState.selectedOperation,
                }));
                handleAdditionalVerification(response.factors);
              } else if (response.factorResult === "PASSWORD_RESET") {
                setChallengeNumber("");
                setVerificationCode("");
                if (!!response.hasOwnProperty("sessionToken"))
                  saveSessionToken(response.sessionToken);
                navigate(ApiUtils.recoveryOptionRoute, {
                  state: ApiUtils.recoveryOptionRoute,
                });
              } else if (
                (response.hasOwnProperty("factorResult") &&
                  response.factorResult.toUpperCase() === "SUCCESS") ||
                (response.hasOwnProperty("status") &&
                  response.status.toUpperCase() === "SUCCESS")
              ) {
                setChallengeNumber("");
                saveSessionToken(response.sessionToken);
                openNotification("success", ApiUtils.pushApprovedMsg);
                if (userState.selectedOperation === "Unlock Account") {
                  if (response.hasOwnProperty("Next")) {
                    userState.userInfo.recovery_question = response.Next;
                    setUserState(userState);
                    navigate(ApiUtils.recoveryQuestionRoute, {
                      state: ApiUtils.recoveryQuestionRoute,
                    });
                  } else {
                    navigate(ApiUtils.confirmationRoute, {
                      state: ApiUtils.confirmationRoute,
                    });
                    return;
                  }
                }
                if (!!userInfo?.recovery_question) {
                  navigate(ApiUtils.recoveryQuestionRoute, {
                    state: ApiUtils.recoveryQuestionRoute,
                  });
                } else {
                  navigate(ApiUtils.recoveryOptionRoute, {
                    state: ApiUtils.recoveryOptionRoute,
                  });
                }
              } else if (response.factorResult === "REJECTED") {
                setChallengeNumber("");
                openNotification("error", "Push Rejected");
                clearSessionStorage();
                navigate("/");
              } else if (response.factorResult === "TIMEOUT") {
                setChallengeNumber("");
                openNotification("info", ApiUtils.pushTimeoutMsg);
              } else if (response.factorResult === "WAITING") {
                setPushSessionToken(response.sessionToken);
                if (
                  !!response.hasOwnProperty("expiresAt") &&
                  new Date().getTime() < new Date(response.expiresAt).getTime()
                )
                  handlePollAPI();
              }
            } else {
              if (response.hasOwnProperty("errorSummary")) {
                setErrorDescription(response.errorSummary);
              }
              focusInputField();
            }
          })
          .catch((error) => {
            stopAllButtonLoadings();
            pollResponse = error;
            openNotification("error", ApiUtils.apiPollForSuccessErrorMsg);
          }),
      5000,
    );
    return pollResponse;
  };
  async function handleVerifyClick() {
    sessionStorage.setItem("stop-poll", "true");
    if (userState.selectedOperation !== ApiUtils.unlockAccountLabel) {
      if (
        selectedFactor?.factorType === ApiUtils.HOTPFactorType ||
        (selectedFactor?.factorType === ApiUtils.yubicoTOTPHardwareType &&
          selectedFactor?.factorProvider ===
            ApiUtils.yubicoTOTPHardwareProvider) ||
        selectedFactor?.factorType === ApiUtils.googleTOTPFactorType ||
        selectedFactor?.factorType === ApiUtils.oktaTOTPFactorType ||
        selectedFactor?.factorType === ApiUtils.questionFactorType
      ) {
        handleSendCodeClick(selectedFactor?.factorType);
      } else {
        if (!!verificationCode) {
          let selected = sessionStorage.getItem("selectedFactor");
          let selected_split: string[] = [];
          if (!!selected) {
            selected_split = selected.split(":");
          }
          try {
            let payload: validateChallengePayload = {
              passCode: !!verificationCode ? verificationCode : "",
              username: !!userInfo?.user_name ? userInfo.user_name : "",
              sessionToken: getSessionToken(),
              factor_provider: selected_split[1],
              factor_type:
                selected_split[1] === "DUO" ? "sms" : selected_split[0],
            };
            console.log(payload);
            await ApiService.post(
              `${ApiUtils.base_url}${ApiUtils.Urls.validateChallenge}`,
              payload,
            )
              .then((response) => {
                stopAllButtonLoadings();
                if (
                  !!verifyErrorAndShowMessage(
                    response,
                    ApiUtils.apiValidateChallengeErrorMsg,
                  )
                ) {
                  if (response.hasOwnProperty("factorResult")) {
                    if (response.factorResult === "ANSWER_RECOVERY_QUESTION") {
                      openNotification(
                        "success",
                        ApiUtils.passcodeVerifySuccesMsg,
                      );
                      if (!!response.hasOwnProperty("sessionToken"))
                        saveSessionToken(response.sessionToken);
                      setSelectedFactor({
                        factorProvider: "",
                        factorStatus: "",
                        factorType: "",
                        hint: "",
                      });
                      if (!!response.hasOwnProperty("sessionToken"))
                        saveSessionToken(response.sessionToken);
                      setUserState((prevState) => ({
                        userInfo: {
                          config: prevState.userInfo.config,
                          currentStep: "ANSWER_RECOVERY_QUESTION",
                          factor: response.factors,
                          recovery_question: response.recovery_question,
                          sessionToken: userState.userInfo.sessionToken,
                          show_change_password:
                            userState.userInfo.show_change_password,
                          show_forgot_password:
                            userState.userInfo.show_forgot_password,
                          show_unlock_account:
                            userState.userInfo.show_unlock_account,
                          user_name: userState.userInfo.user_name,
                        },
                        selectedOperation: userState.selectedOperation,
                      }));
                      navigate(ApiUtils.recoveryQuestionRoute, {
                        state: ApiUtils.recoveryQuestionRoute,
                      });
                    } else if (
                      response.factorResult.toUpperCase() === "SUCCESS"
                    ) {
                      openNotification(
                        "success",
                        ApiUtils.passcodeVerifySuccesMsg,
                      );
                      if (!!response.hasOwnProperty("sessionToken"))
                        saveSessionToken(response.sessionToken);
                      if (!!userInfo?.recovery_question) {
                        navigate(ApiUtils.recoveryQuestionRoute, {
                          state: ApiUtils.recoveryQuestionRoute,
                        });
                      } else {
                        navigate(ApiUtils.recoveryOptionRoute, {
                          state: ApiUtils.recoveryOptionRoute,
                        });
                      }
                    } else if (response.factorResult === "ADDITIONAL_MFA") {
                      openNotification(
                        "success",
                        ApiUtils.passcodeVerifySuccesMsg,
                      );
                      sessionStorage.setItem("selectedFactor", "");
                      setVerificationCode("");
                      setSelectedFactor({
                        factorProvider: "",
                        factorStatus: "",
                        factorType: "",
                        hint: "",
                      });
                      if (!!response.hasOwnProperty("sessionToken"))
                        saveSessionToken(response.sessionToken);
                      setUserState((prevState) => ({
                        userInfo: {
                          config: prevState.userInfo.config,
                          currentStep: "ADDITIONAL_MFA",
                          factor: response.factors,
                          recovery_question: "",
                          sessionToken: userState.userInfo.sessionToken,
                          show_change_password:
                            userState.userInfo.show_change_password,
                          show_forgot_password:
                            userState.userInfo.show_forgot_password,
                          show_unlock_account:
                            userState.userInfo.show_unlock_account,
                          user_name: userState.userInfo.user_name,
                        },
                        selectedOperation: userState.selectedOperation,
                      }));
                      handleAdditionalVerification(response.factors);
                      setLoading(false);
                    } else if (response.factorResult === "PASSWORD_RESET") {
                      openNotification(
                        "success",
                        ApiUtils.passcodeVerifySuccesMsg,
                      );
                      setVerificationCode("");
                      if (!!response.hasOwnProperty("sessionToken"))
                        saveSessionToken(response.sessionToken);
                      navigate(ApiUtils.recoveryOptionRoute, {
                        state: ApiUtils.recoveryOptionRoute,
                      });
                    } else if (response.factorResult === "CHALLENGE") {
                      if (!!response.hasOwnProperty("message")) {
                        setErrorDescription(response.message);
                        openNotification("error", response.message);
                      }
                    }
                  }
                } else {
                  if (response.hasOwnProperty("errorSummary")) {
                    setErrorDescription(response.errorSummary);
                  }
                  focusInputField();
                }
              })
              .catch((error) => {
                stopAllButtonLoadings();
                focusInputField();
                openNotification("info", ApiUtils.apiValidateChallengeErrorMsg);
              })
              .finally(() => {
                stopAllButtonLoadings();
              });
          } catch (e) {
            stopAllButtonLoadings();
            openNotification("info", ApiUtils.apiValidateChallengeErrorMsg);
          }
        } else {
          stopAllButtonLoadings();
          openNotification("info", ApiUtils.oktaTOTPFactorType);
        }
      }
    } else {
      if (
        selectedFactor?.factorType === ApiUtils.HOTPFactorType ||
        (selectedFactor?.factorType === ApiUtils.yubicoTOTPHardwareType &&
          selectedFactor?.factorProvider ===
            ApiUtils.yubicoTOTPHardwareProvider) ||
        (selectedFactor?.factorType === ApiUtils.oktaTOTPFactorType &&
          selectedFactor?.factorProvider === ApiUtils.oktaTOTPFactorProvider) ||
        selectedFactor?.factorType === ApiUtils.oktaTOTPFactorType ||
        selectedFactor?.factorType === ApiUtils.googleTOTPFactorType ||
        selectedFactor?.factorType === ApiUtils.questionFactorType
      ) {
        handleSendCodeClick(selectedFactor.factorType);
      } else {
        if (!verificationCode) {
          stopAllButtonLoadings();
          openNotification("info", ApiUtils.oktaTOTPFactorType);
          return;
        }
        let payload: unlockAccountStepTwoPayload = {
          passCode: verificationCode,
          sessionToken: getSessionToken(),
          username: userState.userInfo.user_name,
          factor_provider: selectedFactor?.factorProvider
            ? selectedFactor?.factorProvider
            : "",
          factor_type: !!selectedFactor?.factorType
            ? selectedFactor?.factorType
            : "",
        };
        await ApiService.post(
          `${ApiUtils.base_url}${ApiUtils.Urls.unlockAccount_stepTwo}`,
          payload,
        )
          .then((response) => {
            stopAllButtonLoadings();
            if (
              !!verifyErrorAndShowMessage(
                response,
                ApiUtils.apiValidateChallengeErrorMsg,
              )
            ) {
              if (
                response.hasOwnProperty("factorResult") &&
                response.factorResult === "SUCCESS"
              ) {
                if (
                  response.hasOwnProperty("question") &&
                  !!response.question
                ) {
                  openNotification("success", ApiUtils.passcodeVerifySuccesMsg);
                  saveSessionToken(response.sessionToken);
                  userState.userInfo.recovery_question = response.question;
                  setUserState(userState);
                  navigate(ApiUtils.recoveryQuestionRoute, {
                    state: ApiUtils.recoveryQuestionRoute,
                  });
                } else {
                  navigate(ApiUtils.confirmationRoute, {
                    state: ApiUtils.confirmationRoute,
                  });
                }
              } else if (
                response.hasOwnProperty("recoveryType") &&
                response.recoveryType === "UNLOCK"
              ) {
                setChallengeNumber("");
                saveSessionToken(response.sessionToken);
                openNotification(
                  "success",
                  selectedFactor?.factorType === ApiUtils.questionFactorType
                    ? ApiUtils.answerVerifySuccesMsg
                    : ApiUtils.passcodeVerifySuccesMsg,
                );
                if (response.hasOwnProperty("Next")) {
                  userState.userInfo.recovery_question = response.Next;
                  setUserState(userState);
                  navigate(ApiUtils.recoveryQuestionRoute, {
                    state: ApiUtils.recoveryQuestionRoute,
                  });
                } else {
                  navigate(ApiUtils.confirmationRoute, {
                    state: ApiUtils.confirmationRoute,
                  });
                }
              } else if (response.factorResult === "ADDITIONAL_MFA") {
                sessionStorage.setItem("selectedFactor", "");
                setVerificationCode("");
                setSelectedFactor({
                  factorProvider: "",
                  factorStatus: "",
                  factorType: "",
                  hint: "",
                });
                if (!!response.hasOwnProperty("sessionToken"))
                  saveSessionToken(response.sessionToken);
                setUserState((prevState) => ({
                  userInfo: {
                    config: prevState.userInfo.config,
                    currentStep: "ADDITIONAL_MFA",
                    factor: response.factors,
                    recovery_question: "",
                    sessionToken: userState.userInfo.sessionToken,
                    show_change_password:
                      userState.userInfo.show_change_password,
                    show_forgot_password:
                      userState.userInfo.show_forgot_password,
                    show_unlock_account: userState.userInfo.show_unlock_account,
                    user_name: userState.userInfo.user_name,
                  },
                  selectedOperation: userState.selectedOperation,
                }));
                handleAdditionalVerification(response.factors);
              } else if (response.factorResult === "PASSWORD_RESET") {
                setVerificationCode("");
                if (!!response.hasOwnProperty("sessionToken"))
                  saveSessionToken(response.sessionToken);
                navigate(ApiUtils.recoveryOptionRoute, {
                  state: ApiUtils.recoveryOptionRoute,
                });
              } else {
                if (
                  response.hasOwnProperty("FactorResult") &&
                  !!response.FactorResult
                ) {
                  stopAllButtonLoadings();
                  openNotification("info", response.FactorResult);
                } else {
                  stopAllButtonLoadings();
                  openNotification(
                    "info",
                    "An error occurred while processing request",
                  );
                }
              }
            } else {
              if (response.hasOwnProperty("errorSummary")) {
                setErrorDescription(response.errorSummary);
              }
              focusInputField();
            }
          })
          .catch((error) => {
            stopAllButtonLoadings();
          });
      }
    }
    setDUOButtonLoadings([false, false, false, false]);
  }
  const [imageBeaconSrc, setImageBeaconSrc] = useState("../../human.png");
  const [loading, setLoading] = useState<boolean>(true);
  return (
    <Skeleton loading={loading}>
      {" "}
      <div className="recovery-homepage-container">
        {!!brandingDetails?.logo ? (
          <div
            className={"factor-authorize-logo factor-authorize-div"}
            style={{
              height: "100px",
              display: "flex",
              flexGrow: 1,
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <img
              alt="Branding"
              src={`${brandingDetails?.logo}`}
              style={{
                maxWidth: "90%",
                height: "80%",
              }}
            />
          </div>
        ) : (
          ""
        )}
        <div className={"factor-authorize-div"}>
          <Divider style={{ border: "1px", borderColor: "#d8d8d8" }}>
            {" "}
            <div
              className={
                "factor-authorize-beacon-container factor-authorize-div"
              }
            >
              <div className={"factor-authorize-beacon-logo"}>
                <img
                  className={"factor-authorize-beacon-image"}
                  alt="Branding"
                  src={imageBeaconSrc}
                ></img>{" "}
              </div>
            </div>
          </Divider>
        </div>
        <div className={"recovery-homepage-div recovery-homepage-branding"}>
          <h1>{userState.selectedOperation}</h1>
        </div>
        <div className={"factor-authorize-fields-container"}>
          <div>
            <h3>
              {!!userState.userInfo.currentStep &&
              userState.userInfo.currentStep === "ADDITIONAL_MFA"
                ? "Additional Verification"
                : ""}
            </h3>
          </div>
          <div className={"factor-authorize-fields"}>
            {!!factorListVisible ? (
              <>
                {" "}
                {userState.userInfo.config.factors_ui_display === "LIST" ? (
                  <List
                    bordered
                    size="small"
                    style={{
                      border: "1px solid #d8d8d8",
                      overflow: "auto",
                      maxHeight: "350px",
                    }}
                    dataSource={authorizedFactors}
                    renderItem={(item) => (
                      <List.Item
                        style={{
                          cursor: "pointer",
                          backgroundColor:
                            selectedListItem === item ? "lightblue" : "white",
                        }}
                        onClick={() => {
                          handleItemClick(item);
                          sessionStorage.setItem(
                            "selectedFactor",
                            item.factorType + ":" + item.factorProvider,
                          );
                          setVerificationCode("");
                          focusInputField();
                          setDuoSectionVisi(
                            item.factorProvider === ApiUtils.DUOFactorProvider
                              ? true
                              : false,
                          );
                          setImageBeaconSrc(getImageBeacon(item));
                          setSelectedFactor(item);
                        }}
                      >
                        <img
                          className={"factor-authorize-item-logo"}
                          alt={item["factorType"]}
                          style={{
                            width: "15%",
                          }}
                          src={getFactorIcon(item)}
                        ></img>{" "}
                        {getFactorDisplayName(item)}
                      </List.Item>
                    )}
                  />
                ) : (
                  <Select
                    placeholder="Please select factor"
                    style={{ width: "100%" }}
                    size="large"
                    defaultValue={defaultFactorToShow}
                    onChange={(value) => {
                      const factorType = value.split("-")[0];
                      const factorProvider = value.split("-")[1];
                      sessionStorage.setItem(
                        "selectedFactor",
                        factorType + ":" + factorProvider,
                      );
                      setDuoSectionVisi(
                        factorProvider === ApiUtils.DUOFactorProvider
                          ? true
                          : false,
                      );
                      setImageBeaconSrc(
                        getImageBeacon({
                          factorType: factorType,
                          factorProvider: factorProvider,
                        }),
                      );
                      if (!!authorizedFactors) {
                        for (const object of authorizedFactors) {
                          if (
                            object.factorType + "-" + object.factorProvider ===
                            value
                          ) {
                            setSelectedFactor(object);
                            break;
                          }
                        }
                      }
                    }}
                  >
                    {authorizedFactors?.map((item) => {
                      return (
                        <Select.Option
                          key={item["factorType"]}
                          value={
                            item["factorType"] + "-" + item["factorProvider"]
                          }
                        >
                          <img
                            alt="img"
                            className={"factor-authorize-list-logo"}
                            src={getFactorIcon(item)}
                          ></img>
                          &nbsp;{getFactorDisplayName(item)}
                        </Select.Option>
                      );
                    })}
                  </Select>
                )}
              </>
            ) : (
              ""
            )}
          </div>
          {!!errorDescription ? (
            <div>
              <Alert
                type="warning"
                showIcon
                description={errorDescription}
              ></Alert>
            </div>
          ) : (
            ""
          )}
          <div className={"factor-homepage-caption factor-homepage-div"}>
            <h4 className={"factor-header-help"}>{selectedOperation}</h4>
          </div>
          <div className={"factor-homepage-caption factor-homepage-div"}>
            <h3 className={""}>
              {selectedFactor && getFactorDisplayName(selectedFactor)}
            </h3>
          </div>
          <div className={"factor-homepage-caption factor-homepage-div"}>
            {selectedFactor && getFactorHint(selectedFactor)}
          </div>
          {selectedFactor?.factorType !== ApiUtils.authentifaceFactorType &&
          selectedFactor?.factorProvider !== "DUO" &&
          !!selectedFactor?.factorType &&
          selectedFactor?.factorType !== ApiUtils.oktaPushFactorType &&
          selectedFactor?.factorProvider !== ApiUtils.CREDENTIFactorProvider ? (
            <>
              <div
                className={"recovery-homepage-username recovery-homepage-div"}
              >
                <div style={{ display: "flex", gap: "10px" }}>
                  <Password
                    id="codeTextField"
                    onKeyUpCapture={(event) => {
                      if (
                        event.key === "Enter" &&
                        selectedFactor.factorProvider === "YUBICO"
                      ) {
                        setButtonLoading(1);
                        handleVerifyClick();
                      }
                    }}
                    style={{ flex: 3 }}
                    placeholder={
                      selectedFactor?.factorType === ApiUtils.questionFactorType
                        ? "Enter answer"
                        : selectedFactor?.factorProvider ===
                              ApiUtils.yubicoTOTPHardwareProvider &&
                            selectedFactor?.factorType ===
                              ApiUtils.yubicoTOTPHardwareType
                          ? ""
                          : "Enter code"
                    }
                    size="large"
                    onChange={(e) => {
                      setVerificationCode(e.target.value);
                    }}
                    value={verificationCode}
                  />
                  {selectedFactor?.factorType === ApiUtils.smsFactorType ||
                  selectedFactor?.factorType === ApiUtils.callFactorType ||
                  selectedFactor?.factorType === ApiUtils.voiceFactorType ||
                  selectedFactor?.factorType === ApiUtils.emailFactorType ? (
                    <Button
                      disabled={sendCodeButtonLabel === "Sent" ? true : false}
                      size="large"
                      type="default"
                      style={{ flex: 1 }}
                      onClick={() => {
                        handleSendCodeClick(selectedFactor.factorType);
                        setButtonLoading(0);
                      }}
                      loading={buttonLoadings[0]}
                    >
                      {sendCodeButtonLabel}
                    </Button>
                  ) : (
                    ""
                  )}
                </div>
              </div>
              {/* verify sms code button */}
              <div className="factory-homepage-button factory-homepage-div">
                <Button
                  className={""}
                  type="primary"
                  size="large"
                  onClick={() => {
                    setButtonLoading(1);
                    handleVerifyClick();
                  }}
                  loading={buttonLoadings[1]}
                >
                  Verify
                </Button>
              </div>
            </>
          ) : (
            ""
          )}
          {!!challengeNumber ? (
            <div className={"number-challenge-section"}>
              <p>
                On your mobile device, open the Okta Verify prompt, then tap{" "}
                <b>{challengeNumber}</b> in Okta Verify to continue.
              </p>
              <div className={"phone"}>
                <div className={"phone-body"}>
                  <div className={"phone--screen"}>
                    <span
                      className={"phone--number"}
                      data-se="challenge-number"
                    >
                      {challengeNumber}
                    </span>
                  </div>
                  <div className={"phone--home-button"}></div>
                </div>
              </div>
            </div>
          ) : (
            ""
          )}
          {!challengeNumber &&
          selectedFactor?.factorProvider !== "DUO" &&
          selectedFactor?.factorType === ApiUtils.oktaPushFactorType &&
          selectedFactor?.factorProvider === ApiUtils.oktaPushFactorProvider ? (
            <div className="factory-homepage-button factory-homepage-div">
              <Button
                disabled={sendPushButtonLabel === "Push sent!" ? true : false}
                className={""}
                type="primary"
                size="large"
                loading={buttonLoadings[2]}
                onClick={() => {
                  handleSendCodeClick(ApiUtils.oktaPushFactorType);
                  setButtonLoading(2);
                }}
              >
                {sendPushButtonLabel}
              </Button>
            </div>
          ) : (
            ""
          )}
          {selectedFactor?.factorProvider === ApiUtils.CREDENTIFactorProvider &&
          selectedFactor?.factorType === ApiUtils.FACE_AUTHFactoryType ? (
            <>
              <div style={{ display: "flex" }}></div>
              {!!qrCodeValue && (
                <>
                  <div style={{ position: "relative", marginTop: "10px" }}>
                    <QRCode size={140} value={qrCodeValue} />
                  </div>
                </>
              )}
              <Button
                type="primary"
                size="large"
                disabled={!retryTecproof}
                loading={buttonLoadings[0]}
                onClick={handleGenerateTecproof}
              >
                Retry{!retryTecproof && countDown > 0 && ` ${countDown}`}
              </Button>
            </>
          ) : (
            ""
          )}
          {/* DUO factor section */}
          {!!duoSectionVisi ? (
            <div
              style={{
                padding: "10px",
                backgroundColor: "#f7f7f7",
                border: "1px solid #d8d8d8",
                marginTop: "10px",
              }}
            >
              <Button
                loading={duoButtonLoadings[0]}
                onClick={() => {
                  setSelectedFactor(
                    DUOFactorList.find(
                      (item) =>
                        item.factorProvider ===
                          ApiUtils.DUOPushFactorProvider &&
                        item.factorType === ApiUtils.DUOPushFactorType,
                    ),
                  );
                  setDuoPasscodeSectionVisi(false);
                  setDUOButtonLoadings([true, false, false, false]);
                  handleSendCodeClick(ApiUtils.DUOPushFactorType, "push");
                }}
                style={{
                  marginRight: "10px",
                  marginBottom: "10px",
                  background: "#63B246",
                  borderColor: "yellow",
                }}
                size="large"
                disabled={sendPushButtonLabel === "Push sent!" ? true : false}
                type="primary"
              >
                {sendPushButtonLabel}
              </Button>
              <Button
                loading={duoButtonLoadings[1]}
                onClick={() => {
                  document.getElementById("duoCodeField")?.focus();
                  setSelectedFactor(
                    DUOFactorList.find(
                      (item) =>
                        item.factorProvider ===
                          ApiUtils.DUOTOTPFactorProvider &&
                        item.factorType === ApiUtils.DUOTOTPFactorType,
                    ),
                  );
                  setDuoPasscodeSectionVisi(true);
                }}
                style={{
                  marginRight: "10px",
                  marginBottom: "10px",
                  background: "#63B246",
                  borderColor: "yellow",
                }}
                size="large"
                type="primary"
              >
                Enter a passcode
              </Button>
              {!!duoPasscodeSectionVisi ? (
                <div style={{}}>
                  <Password
                    size="large"
                    style={{ marginBottom: "10px" }}
                    id="duoCodeField"
                    value={verificationCode}
                    onChange={(e) => {
                      setVerificationCode(e.target.value);
                    }}
                    placeholder={"Enter Passcode"}
                  ></Password>
                  <div style={{ display: "flex" }}>
                    <Button
                      onClick={() => {
                        setDUOButtonLoadings([false, false, true, false]);
                        handleVerifyClick();
                      }}
                      loading={duoButtonLoadings[2]}
                      size="large"
                      type="primary"
                      style={{
                        width: "75%",
                        marginRight: "10px",
                        marginBottom: "10px",
                        background: "#63B246",
                        borderColor: "yellow",
                      }}
                    >
                      Submit
                    </Button>
                    <Button
                      onClick={() => {
                        setSelectedFactor(
                          DUOFactorList.find(
                            (item) =>
                              item.factorProvider ===
                                ApiUtils.DUOFactorProvider &&
                              item.factorType === ApiUtils.DUOFactorType,
                          ),
                        );
                        setDUOButtonLoadings([false, false, false, true]);
                        handleSendCodeClick(
                          ApiUtils.DUOSMSFactorType,
                          ApiUtils.DUOSMSFactorType,
                        );
                      }}
                      disabled={sendCodeButtonLabel === "Sent" ? true : false}
                      loading={duoButtonLoadings[3]}
                      size="large"
                      type="primary"
                      style={{
                        width: "40%",
                        marginBottom: "10px",
                        background: "#63B246",
                        borderColor: "yellow",
                      }}
                    >
                      {sendCodeButtonLabel}
                    </Button>
                  </div>
                </div>
              ) : (
                ""
              )}
            </div>
          ) : (
            ""
          )}

          <div style={{ paddingTop: "15px", paddingBottom: "10px" }}>
            <nav>
              <Link
                to={ApiUtils.recoveryHomePageRoute}
                onClick={() => {
                  clearSessionStorage();
                }}
              >
                <u>Back to recovery</u>
              </Link>
            </nav>
            {!!brandingDetails?.instructions ? (
              <div>
                <div
                  style={{
                    display: "flex",
                    color: "grey",
                  }}
                >
                  <div>Help/Instructions:</div>
                </div>
                <div
                  style={{
                    display: "flex",
                    color: "grey",
                  }}
                >
                  <div>
                    <RichTextViewer content={content} />
                  </div>
                </div>
              </div>
            ) : (
              ""
            )}
          </div>
        </div>
      </div>
    </Skeleton>
  );
}
